// src/utils/autoplayPolicy.ts
import { log } from "./debug";
export type AutoplayAction = "allowed" | "muted" | "blocked";

export interface AutoplayPolicyDetail {
  section?: HTMLElement | null;
  video?: HTMLVideoElement | null;
  action: AutoplayAction;
  reason?: string;
  ts?: number; // debug timing
}

export type AutoplayPolicyEvent = CustomEvent<AutoplayPolicyDetail>;

export const AUTOPLAY_POLICY_EVENT = "sc:autoplay-policy";

export type Unsubscribe = () => void;

/** SSR-safe document presence check */
const hasDoc = () => typeof document !== "undefined";

/** Dispatch the canonical autoplay policy event. */
export function emitAutoplayPolicy(detail: AutoplayPolicyDetail): void {
  if (!hasDoc()) return;
  const ts = typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
  const enriched: AutoplayPolicyDetail = { ts, ...detail };
  document.dispatchEvent(new CustomEvent<AutoplayPolicyDetail>(AUTOPLAY_POLICY_EVENT, { detail: enriched }));
}

/** Subscribe to the canonical policy event. Returns an unsubscribe fn. */
export function onAutoplayPolicy(
  listener: (evt: AutoplayPolicyEvent) => void,
  opts?: { signal?: AbortSignal; capture?: boolean; once?: boolean }
): Unsubscribe {
  if (!hasDoc()) return () => {};
  const handler = (e: Event) => listener(e as AutoplayPolicyEvent);
  const capture = !!opts?.capture;
  const once = !!opts?.once;
  document.addEventListener(AUTOPLAY_POLICY_EVENT, handler as EventListener, { passive: true, capture, once });

  let unsubbed = false;
  const unsubscribe = () => {
    if (unsubbed || !hasDoc()) return;
    unsubbed = true;
    document.removeEventListener(AUTOPLAY_POLICY_EVENT, handler as EventListener, { capture });
  };

  if (opts?.signal) {
    if (opts.signal.aborted) unsubscribe();
    else opts.signal.addEventListener("abort", unsubscribe, { once: true });
  }

  return unsubscribe;
}

export interface OnAutoplayPolicyForSectionOptions {
  strict?: boolean;
  signal?: AbortSignal;
  capture?: boolean;
  once?: boolean;
}

/** Subscribe only for a specific section element. */
export function onAutoplayPolicyForSection(
  section: HTMLElement,
  listener: (evt: AutoplayPolicyEvent) => void,
  opts?: OnAutoplayPolicyForSectionOptions
): Unsubscribe {
  const strict = !!opts?.strict;
  return onAutoplayPolicy((evt) => {
    const sec = evt.detail.section;
    if (strict) {
      // strict: ignore if no section or mismatch
      if (!sec || sec !== section) return;
    } else {
      // default: broadcast events (no section) pass through
      if (sec && sec !== section) return;
    }
    listener(evt);
  }, { signal: opts?.signal, capture: opts?.capture, once: opts?.once });
}

// ---- Legacy bridge (idempotent across HMR / multi-bundle) ----
export function normalizeLegacyAutoplayEvents(): () => void {
  if (!hasDoc()) return () => {};

  const g = globalThis as any;
  g.__scAutoplayBridgeRef = g.__scAutoplayBridgeRef ?? 0;

  // If already active in this runtime, just bump refcount and return a lightweight unsubscribe.
  if (g.__scAutoplayBridgeActive) {
    g.__scAutoplayBridgeRef++;
    return () => {
      g.__scAutoplayBridgeRef = Math.max(0, (g.__scAutoplayBridgeRef as number) - 1);
      // No listener removal here; the owning instance will clean up when ref hits 0.
    };
  }

  // First activator owns the listeners.
  g.__scAutoplayBridgeActive = true;
  g.__scAutoplayBridgeRef++;

  const blocked = (e: any) =>
    emitAutoplayPolicy({
      section: e?.detail?.section ?? null,
      video: e?.detail?.video ?? null,
      action: "blocked",
      reason: e?.detail?.reason || e?.detail?.error || "blocked",
    });

  const muted = (e: any) =>
    emitAutoplayPolicy({
      section: e?.detail?.section ?? null,
      video: e?.detail?.video ?? null,
      action: "muted",
      reason: e?.detail?.reason || e?.detail?.policy || "muted",
    });

  const allowed = (e: any) =>
    emitAutoplayPolicy({
      section: e?.detail?.section ?? null,
      video: e?.detail?.video ?? null,
      action: "allowed",
      reason: e?.detail?.reason || "allowed",
    });

  const hBlocked1 = blocked as EventListener;
  const hBlocked2 = blocked as EventListener;
  const hMuted   = muted   as EventListener;
  const hAllowed = allowed as EventListener;

  document.addEventListener("sc:autoplay-failed-unmute",     hBlocked1, { passive: true, capture: false });
  document.addEventListener("sc:autoplay-with-audio-failed", hBlocked2, { passive: true, capture: false });
  document.addEventListener("sc:autoplay-muted",             hMuted,    { passive: true, capture: false });
  document.addEventListener("sc:autoplay-with-audio-allowed",hAllowed,  { passive: true, capture: false });

  // Unsubscribe decrements refcount; only the owner removes listeners when ref hits 0.
  return () => {
    g.__scAutoplayBridgeRef = Math.max(0, (g.__scAutoplayBridgeRef as number) - 1);
    if (g.__scAutoplayBridgeRef === 0) {
      document.removeEventListener("sc:autoplay-failed-unmute",     hBlocked1, { capture: false } as any);
      document.removeEventListener("sc:autoplay-with-audio-failed", hBlocked2, { capture: false } as any);
      document.removeEventListener("sc:autoplay-muted",             hMuted,    { capture: false } as any);
      document.removeEventListener("sc:autoplay-with-audio-allowed",hAllowed,  { capture: false } as any);
      g.__scAutoplayBridgeActive = false;
    }
  };
}

/**
 * Optional dev tracer for policy events. No-op when disabled or SSR.
 */
export function devTraceAutoplayPolicy(enable = false): Unsubscribe {
  if (!enable || !hasDoc()) return () => {};
  return onAutoplayPolicy(e => {
    try {
      // eslint-disable-next-line no-console
      log("[policy]", e.detail.action, e.detail.reason ?? "", e.detail.ts);
    } catch {}
  });
}